import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  TimePicker,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import useGetMapLatLngFromShortLink from '@/hooks/shipping-address';
import { IDataModal } from '@/interfaces/dataModal';
import { IShippingAddress } from '@/interfaces/shipping-address';
import GeographySelector from '@/components/selector/geography-selector';
import { IGeography } from '@/interfaces/geography';
import {
  addShippingAddress,
  editShippingAddress,
} from '@/adapters/shipping-address';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';
import CountrySelector from '@/components/selector/country-selector';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  MapCameraChangedEvent,
  Pin,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';

interface IProps {
  customerId: number;
  dataModal?: IDataModal<Partial<IShippingAddress>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const ShippingAddressModal = ({
  customerId,
  dataModal,
  handleOk,
  handleCancel,
}: IProps) => {
  const [form] = Form.useForm<Partial<IShippingAddress>>();
  const link = Form.useWatch('maplink', form);
  const [mapLink, setMapLink] = useState<string | undefined>();
  const { refetch, isFetching, data } = useGetMapLatLngFromShortLink(mapLink, {
    enabled: false,
    retry: 3,
  });
  const mutations = useMutation({
    mutationFn: async (data: Partial<IShippingAddress>) =>
      dataModal?.id
        ? await editShippingAddress(data)
        : await addShippingAddress(data),
    mutationKey: ['action-shipping-addfress'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        const message = dataModal?.id
          ? 'อัพเดทที่อยู่สำเร็จ'
          : 'สร้างที่อยู่สำเร็จ';
        notification.success({
          message: message,
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const [point, setPoint] = useState<{
    lat: number;
    lng: number;
  }>({
    lat: Number(dataModal?.item?.lat || 0),
    lng: Number(dataModal?.item?.lng || 0),
  });

  const [center, setCenter] = useState({
    lat: Number(dataModal?.item?.lat || 13.5769583),
    lng: Number(dataModal?.item?.lng || 100.7678161),
  });

  const onMapClick = (e: MapMouseEvent) => {
    setPoint({
      lat: e.detail?.latLng?.lat || 0,
      lng: e?.detail?.latLng?.lng || 0,
    });
    form.setFieldsValue({
      lat: e.detail?.latLng?.lat?.toString() || '',
      lng: e?.detail?.latLng?.lng?.toString() || '',
      maplink: `https://maps.google.com/?q=${e.detail?.latLng?.lat},${e.detail?.latLng?.lng}`,
    });
  };

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IShippingAddress>) => {
        values.id = Number(dataModal?.id || 0);
        values.entryId = customerId;
        values.entryName = 'customer';
        values.dueDateTime = dayjs(values.dueDateTime).format('HH:mm');
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
    setPoint({ lat: 0, lng: 0 });
    setCenter({ lat: 13.5769583, lng: 100.7678161 });
    setMapLink(undefined);
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
        dueDateTime:
          (dataModal?.item?.dueDateTime &&
            dayjs(dataModal?.item?.dueDateTime, 'HH:mm')) ||
          undefined,
      });
      setPoint({
        lat: Number(dataModal?.item?.lat) || 0,
        lng: Number(dataModal?.item?.lng) || 0,
      });
      setCenter({
        lat: Number(dataModal?.item?.lat) || 13.5769583,
        lng: Number(dataModal?.item?.lng) || 100.7678161,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  const onSelectGeography = (item: IGeography, locale: 'th' | 'en') => {
    form.setFieldsValue({
      ...item,
      subdistrictNameTh:
        locale === 'th' ? item.subdistrictNameTh : item.subdistrictNameEn,
      districtNameTh:
        locale === 'th' ? item.districtNameTh : item.districtNameEn,
      provinceNameTh:
        locale === 'th' ? item.provinceNameTh : item.provinceNameEn,
      subdistrictCode: item.subdistrictCode,
      districtCode: item.districtCode,
      provinceCode: item.provinceCode,
      country: 'Thailand',
    });
  };

  const handleFetchLatLng = async () => {
    console.log(link);
    if (!link) {
      notification.warning({ message: 'โปรดระบุ Google Map link' });
      return;
    }
    setMapLink(link);
    await refetch();
    // try {
    //   const res = await refetch();
    //   if (res?.data?.status === 200) {
    //     const lat = res.data?.data?.lat;
    //     const lng = res.data?.data?.lng;
    //     setPoint({
    //       lat: Number(res.data?.data?.lat || 0),
    //       lng: Number(res.data?.data?.lng || 0),
    //     });
    //     setCenter({
    //       lat: Number(res.data?.data?.lat || 100.7678161),
    //       lng: Number(res.data?.data?.lng || 100.7678161),
    //     });
    //     if (lat && lng) {
    //       // form.setFieldsValue({ lat, lng });
    //       notification.success({ message: 'นำค่าพิกัดจากลิงก์สำเร็จ' });
    //     } else {
    //       notification.error({ message: 'ไม่พบพิกัดจากลิงก์' });
    //       await refetch();
    //     }
    //   } else {
    //     notification.error({ message: 'ไม่สามารถดึงพิกัดได้' });
    //     await refetch();
    //   }
    // } catch (err) {
    //   notification.error({ message: 'เกิดข้อผิดพลาดในการเรียก API' });
    // }
  };

  console.log('if(data) =? ', data);

  useEffect(() => {
    if (data?.status === 200 && data?.data?.lat) {
      const lat = data?.data?.lat;
      const lng = data?.data?.lng;
      if (lat && lng) {
        form.setFieldsValue({ lat, lng });
        setPoint({
          lat: Number(data?.data?.lat || 0),
          lng: Number(data?.data?.lng || 0),
        });
        setCenter({
          lat: Number(data?.data?.lat || 100.7678161),
          lng: Number(data?.data?.lng || 100.7678161),
        });
        notification.success({ message: 'นำค่าพิกัดจากลิงก์สำเร็จ' });
      } else {
        notification.error({ message: 'ไม่พบพิกัดจากลิงก์' });
      }
    }
  }, [data]);

  return (
    <Modal
      title={'ที่อยู่ขนส่ง'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={1200}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="productGroup"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Form.Item<IShippingAddress> name="subdistrictCode" hidden>
            <Input size="large" hidden />
          </Form.Item>
          <Form.Item<IShippingAddress> name="districtCode" hidden>
            <Input size="large" hidden />
          </Form.Item>
          <Form.Item<IShippingAddress> name="provinceCode" hidden>
            <Input size="large" hidden />
          </Form.Item>
          <Row gutter={12}>
            <Col span={24} md={12}>
              <Form.Item
                label="ชื่อย่อ"
                name="shortName"
                className="w-full"
                rules={[{ required: true, message: 'ระบุชื่อย่อ' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                label="ผู้ติดต่อ"
                name="contactName"
                className="w-full"
                rules={[{ required: true, message: 'ระบุผู้ติดต่อ' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item label="Email" name="email" className="w-full">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <div className="flex w-full flex-row items-end gap-3">
                <div className="flex w-full">
                  <Form.Item
                    label="สถานที่"
                    name="name"
                    rules={[{ required: true, message: 'ระบุสถานที่' }]}
                    className="w-full"
                  >
                    <Input size="large" />
                  </Form.Item>
                </div>
                <div className="flex">
                  <Form.Item label="" name="">
                    <GeographySelector
                      handleSelectData={onSelectGeography}
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>
            </Col>
            <Col span={24} md={12}>
              <Form.Item label="ที่อยู่" name="address" className="w-full">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item label="ถนน" name="road">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="ตำบล" name="subdistrictNameTh">
                <Input size="large" />
              </Form.Item>
              <Form.Item label="ตำบล" name="subdistrictNameEn" hidden>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="อำเภอ" name="districtNameTh">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="จังหวัด" name="provinceNameTh">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="รหัสไปรษณีย์" name="postalCode">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="ประเทศ"
                name="country"
                rules={[{ required: true, message: 'ระบุประเทศ' }]}
              >
                <CountrySelector
                  handleSelectData={function (id: string): void {
                    form.setFieldsValue({
                      country: id,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Google Map" name="maplink">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => handleFetchLatLng()}
                  loading={isFetching}
                  size="large"
                >
                  ค้นหา
                </Button>
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item label="โทรศัพท์" name={'phoneNumber'}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="จัดส่งภายใน" name={'dueDateTime'}>
                <TimePicker
                  size="large"
                  format="HH:mm"
                  value={dayjs(form.getFieldValue('dueDateTime'), 'HH:mm')}
                  minuteStep={30}
                  locale={locale}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item<IShippingAddress> label="Latitude" name={'lat'}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item<IShippingAddress> label="Longtitude" name={'lng'}>
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col md={12}>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item
                    label="สถานะ"
                    name="isActive"
                    rules={[{ required: true, message: 'เลือกสถานะ' }]}
                    initialValue={true}
                  >
                    <Radio.Group>
                      <Radio value={true}>ใช้งาน</Radio>
                      <Radio value={false}>ไม่ใช้งาน</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="หมายเหตุ" name={'remark'}>
                    <Input.TextArea rows={2} size="large" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col md={12}>
              <div
                style={{
                  height: 370,
                  width: '100%',
                  marginBottom: 10,
                }}
              >
                <APIProvider
                  apiKey={'AIzaSyC3LUICKk1b104OLYosRFxRgOYysgSrD_o'}
                  onLoad={() => console.log('Maps API has loaded.')}
                >
                  <Map
                    defaultZoom={12}
                    defaultCenter={center}
                    onClick={onMapClick}
                    onCameraChanged={(ev: MapCameraChangedEvent) => {
                      setCenter(ev.detail.center);
                    }}
                    mapId="x12"
                    center={center}
                  >
                    {point?.lat != 0 && (
                      <AdvancedMarker position={point} clickable={true}>
                        <Pin
                          background={'#FBBC04'}
                          glyphColor={'#000'}
                          borderColor={'#000'}
                        />
                      </AdvancedMarker>
                    )}
                  </Map>
                </APIProvider>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default ShippingAddressModal;
